/**
 * src/services/spatial/spatialEventsClient.ts
 *
 * The browser's view of the Phase 5 event route surface.
 *
 * A transport only. Every judgement this could make is made on the
 * server: whether the caller may edit an event, whether an action is
 * eligible, whether a broadcast is live, how many people are present.
 * The point of routing all of it through one thin client is that the
 * UI and the AI end up reading the same answers, rather than each
 * assembling its own from parts.
 *
 * Shaped after `spatialPresenceClient`, which serves the same base and
 * the same discriminated result, so a caller handling one handles the
 * other. No auth header: the server resolves authorization from the
 * verified session it already has, and a header invented here would be
 * a claim rather than a credential.
 */

import { V2_API } from '../../v2/config';

const BASE = `${V2_API}/spatial-events`;

/** Same discriminated result the presence client uses. */
export type EventResult<T> =
  | ({ ok: true } & T)
  | { ok: false; reason: string; [key: string]: unknown };

export type EventLifecycleVerb =
  | 'pause' | 'resume' | 'cancel' | 'complete' | 'archive';

/** What the server says an event is. Mirrors `eventModel.normalize`. */
export interface EventRecord {
  title?: string | null;
  description?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  status?: string | null;
  visibility?: string | null;
  capacity?: number | null;
  image?: string | null;
  tags?: string[] | null;
  /**
   * A name and an optional link.
   *
   * The server normalizes a bare string into this shape, so a reader
   * never sees the string form even though a form may send one.
   */
  organizer?: { name?: string | null; url?: string | null } | null;
  venue?: string | null;
  floorRef?: string | null;
  zoneRef?: string | null;
  parentEventId?: string | null;
  templateId?: string | null;
  contractId?: string | null;
  contractFunction?: string | null;
  executionRuleId?: string | number | null;
  contentRef?: string | null;
  objectIds?: string[] | null;
}

/** A running broadcast, as much of it as a viewer is told. */
export interface EventLive {
  status?: string | null;
  broadcasterId?: string | null;
  sessionId?: string | null;
  transport?: string | null;
  visibility?: string | null;
  title?: string | null;
  startedAt?: string | null;
  scheduledEndAt?: string | null;
}

/** What the event's data implies about a broadcast. Reported, not applied. */
export interface EventBroadcastRelation {
  id: string;
  when: string;
  then: string;
  holds: boolean;
  automated: boolean;
  automatedBy: string | null;
  notAutomatedBecause: string | null;
  awaitingOperator: boolean;
}

export interface EventDetail {
  eventId: string;
  hostObjectId: string | null;
  event: EventRecord;
  /** The operator's intent. */
  lifecycle: string | null;
  /** The clock's reading. Null when the event does not present as one. */
  state: string | null;
  /** Whether there is actually a room. None of the three defines another. */
  live: EventLive | null;
  liveSource: 'event' | 'host' | null;
  broadcastRelations?: EventBroadcastRelation[];
}

export interface EventPresenceCounts {
  eventCount: number;
  states: Record<string, number>;
  roles: Record<string, number>;
  total: number;
}

// ── Plumbing ────────────────────────────────────────────────────────

async function call<T>(path: string, init?: RequestInit): Promise<EventResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch {
    // A network failure is not a refusal, and calling it one would let
    // a dropped connection read as "you may not do this".
    return { ok: false, reason: 'network_unavailable' };
  }

  let body: unknown = null;
  try { body = await response.json(); } catch { body = null; }

  if (!response.ok) {
    const reason = (body as { reason?: string } | null)?.reason;
    return {
      ...(body as object || {}),
      ok: false,
      reason: reason || `http_${response.status}`,
    } as EventResult<T>;
  }
  return (body || { ok: true }) as EventResult<T>;
}

function postJson<T>(path: string, body?: unknown): Promise<EventResult<T>> {
  return call<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

function patchJson<T>(path: string, body?: unknown): Promise<EventResult<T>> {
  return call<T>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

function del<T>(path: string): Promise<EventResult<T>> {
  return call<T>(path, { method: 'DELETE' });
}

// ── Reading ─────────────────────────────────────────────────────────

/** One event, with its three states reported separately. */
export function getEvent(eventId: string): Promise<EventResult<EventDetail>> {
  return call<EventDetail>(`/${encodeURIComponent(eventId)}`);
}

/**
 * Events near a point, or at a host.
 *
 * `includeLive` is opt-in for the same reason it is on the server: a
 * list of next month's events does not need to know what is on air.
 */
export function listEvents(input: {
  lat?: number;
  lng?: number;
  radiusMeters?: number;
  hostObjectId?: string;
  parentEventId?: string;
  occasion?: string;
  includeLive?: boolean;
  limit?: number;
} = {}): Promise<EventResult<{ events: EventDetail[]; searchedRadiusMeters?: number }>> {
  const query = new URLSearchParams();
  if (Number.isFinite(input.lat)) query.set('lat', String(input.lat));
  if (Number.isFinite(input.lng)) query.set('lng', String(input.lng));
  if (Number.isFinite(input.radiusMeters)) query.set('radiusMeters', String(input.radiusMeters));
  if (input.hostObjectId) query.set('hostObjectId', input.hostObjectId);
  if (input.parentEventId) query.set('parentEventId', input.parentEventId);
  if (input.occasion) query.set('occasion', input.occasion);
  if (input.includeLive) query.set('includeLive', '1');
  if (Number.isFinite(input.limit)) query.set('limit', String(input.limit));
  const suffix = query.toString();
  return call(`/${suffix ? `?${suffix}` : ''}`);
}

/** Live counts for one event. Integers only, by design on the server. */
export function getEventPresence(
  eventId: string,
): Promise<EventResult<EventPresenceCounts>> {
  return call(`/${encodeURIComponent(eventId)}/presence`);
}

/**
 * Everyone who ever declared anything at this event.
 *
 * The history read, which is what makes a completed event answerable
 * after its live presence has been released.
 */
export function getEventPresenceHistory(eventId: string): Promise<EventResult<{
  checkedIn: number; attending: number; present: number; total: number;
}>> {
  return call(`/${encodeURIComponent(eventId)}/presence/history`);
}

/**
 * Counts for many events at once.
 *
 * A POST because the id list can be long, not because it changes
 * anything. This is what feeds the map.
 */
export function getEventCounts(
  eventIds: readonly string[],
): Promise<EventResult<{ counts: Record<string, number> }>> {
  return postJson('/presence/counts', { eventIds });
}

/**
 * Report where the caller is relative to an event.
 *
 * `inside` is resolved by the server from the containment service, not
 * from anything sent here — a client that could declare itself inside
 * would make a check-in a measure of button presses.
 */
export function reportEventPresence(eventId: string, input: {
  participantId: string;
  lat?: number;
  lng?: number;
  intent?: 'observe' | 'check_in' | 'attend';
}): Promise<EventResult<{ state?: string; counts?: EventPresenceCounts }>> {
  return postJson(`/${encodeURIComponent(eventId)}/presence`, input);
}

// ── Maintenance ─────────────────────────────────────────────────────

/**
 * Create an event at a host object.
 *
 * `event` carries only what the form collected. The server decides
 * which fields the caller is allowed to have sent, and a field that
 * needs the venue's agreement is refused there rather than hidden
 * here.
 */
export function createEvent(input: {
  hostObjectId: string;
  wallet?: string;
  event: EventRecord;
  geometry?: unknown;
  name?: string;
}): Promise<EventResult<{ eventId: string; event: EventRecord }>> {
  return postJson('/', input);
}

export function editEvent(eventId: string, input: {
  wallet?: string;
  event: EventRecord;
}): Promise<EventResult<{ event: EventRecord }>> {
  return patchJson(`/${encodeURIComponent(eventId)}`, input);
}

export function rescheduleEvent(eventId: string, input: {
  wallet?: string;
  startsAt: string | null;
  endsAt?: string | null;
  timezone?: string | null;
}): Promise<EventResult<{ event: EventRecord }>> {
  return postJson(`/${encodeURIComponent(eventId)}/reschedule`, input);
}

/**
 * Move the event.
 *
 * Writes the event's own geometry. It cannot move the venue: that is a
 * different object with a different owner, and the server refuses.
 */
export function relocateEvent(eventId: string, input: {
  wallet?: string;
  geometry?: unknown;
  venue?: string | null;
  floorRef?: string | null;
  zoneRef?: string | null;
}): Promise<EventResult<{ event: EventRecord }>> {
  return postJson(`/${encodeURIComponent(eventId)}/relocate`, input);
}

/** pause | resume | cancel | complete | archive. */
export function runEventLifecycle(
  eventId: string,
  verb: EventLifecycleVerb,
  input: { wallet?: string; reason?: string | null } = {},
): Promise<EventResult<{ event: EventRecord; status: string }>> {
  return postJson(`/${encodeURIComponent(eventId)}/${verb}`, input);
}

export function duplicateEvent(eventId: string, input: {
  wallet?: string;
  title?: string;
  startsAt?: string | null;
  endsAt?: string | null;
} = {}): Promise<EventResult<{ eventId: string; event: EventRecord }>> {
  return postJson(`/${encodeURIComponent(eventId)}/duplicate`, input);
}

export function attachEventObject(eventId: string, input: {
  wallet?: string; objectId: string;
}): Promise<EventResult<{ event: EventRecord }>> {
  return postJson(`/${encodeURIComponent(eventId)}/objects`, input);
}

export function detachEventObject(
  eventId: string, objectId: string,
): Promise<EventResult<{ event: EventRecord }>> {
  return del(`/${encodeURIComponent(eventId)}/objects/${encodeURIComponent(objectId)}`);
}

export function attachEventContract(eventId: string, input: {
  wallet?: string; contractId: string; contractFunction?: string | null;
}): Promise<EventResult<{ event: EventRecord }>> {
  return postJson(`/${encodeURIComponent(eventId)}/contract`, input);
}

export function detachEventContract(
  eventId: string,
): Promise<EventResult<{ event: EventRecord }>> {
  return del(`/${encodeURIComponent(eventId)}/contract`);
}

export function attachEventExecutionRule(eventId: string, input: {
  wallet?: string; executionRuleId: string | number;
}): Promise<EventResult<{ event: EventRecord; linked?: boolean; linkReason?: string }>> {
  return postJson(`/${encodeURIComponent(eventId)}/execution-rule`, input);
}

export function detachEventExecutionRule(
  eventId: string,
): Promise<EventResult<{ event: EventRecord }>> {
  return del(`/${encodeURIComponent(eventId)}/execution-rule`);
}
